import React from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactComponent as TeamWorkspaceSVG } from "./Team workspace4.svg";
import { ReactComponent as TemplateCreateSVG } from "./template-create-icon3.svg";
import { ReactComponent as GuideWizardSVG } from "./guide-wizard-icon3.svg";
import { ReactComponent as EmptyWorkspaceSVG } from "./empty_workspace_color_final.svg";

/* =========================================
   TOM ARBETSYTA – PLAIN (ingen glas/ram/gradient)
   ========================================= */
export const EmptyWorkspaceHero = ({
  mode = "portrait",
  fit = "width",
  className,
}) => {
  const ratio =
    mode === "square" ? "1 / 1" : mode === "landscape" ? "16 / 9" : "2 / 3";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 640,
        ...(fit === "height"
          ? { height: "100%", display: "flex" }
          : { aspectRatio: ratio }),
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",     // <-- plain
        borderRadius: 0,               // <-- ingen vit ram/kantskugga
        overflow: "visible",           // låt SVG:ns egna blobs synas fritt
      }}
      className={className}
    >
      <EmptyWorkspaceSVG
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          width: fit === "height" ? "auto" : "92%",
          height: fit === "height" ? "92%" : "auto",
        }}
        aria-label="Tom arbetsyta – en låda som kan fyllas med innehåll"
      />
      {/* inga tint- eller glaslager här */}
    </Box>
  );
};


/* =========================================
   FRÅN GUIDE (wizard) – med bakgrund och bubblor
   ========================================= */
export const GuideWizardHero = ({
  mode = "portrait",
  tint,
  strength = 0.18,
  fit = "width", // "height" fyller höjden på behållaren
  className,
}) => {
  const ratio =
    mode === "landscape" ? "16 / 9" : mode === "square" ? "1 / 1" : "2 / 3"; // något kortare än 3/5

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 640,
        ...(fit === "height" ? { height: "100%", display: "flex" } : { aspectRatio: ratio }),
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(160deg, #ede7f6 0%, #d1c4e9 100%)", // mjuk lila gradient
        borderRadius: 3,
        overflow: "hidden",
      }}
      className={className}
    >
      {/* Dekorativa bubblor */}
      <Box
        sx={{
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "rgba(103, 58, 183, 0.15)", // lila
          top: 40,
          left: 40,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: "rgba(20, 179, 235, 0.15)", // teal
          bottom: 60,
          right: 80,
        }}
      />

      {/* Din SVG-ikon */}
      <GuideWizardSVG
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          position: "relative",
          zIndex: 1,
          width: fit === "height" ? "auto" : "88%",
          height: fit === "height" ? "88%" : "auto",
        }}
        aria-label="Från guide – stegvis guide som bygger arbetsyta"
      />

      {/* (valfri) tint över bakgrunden */}
      {tint ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `
              radial-gradient(60% 60% at 75% 25%, ${alpha(tint, strength)} 0%, transparent 70%),
              linear-gradient(135deg, ${alpha(tint, strength * 0.7)} 0%, transparent 60%)
            `,
            mixBlendMode: "soft-light",
          }}
        />
      ) : null}
    </Box>
  );
};

/* =========================================
   SKAPA MALL / IMPORTERA – med fit-stöd
   ========================================= */
export const TemplateCreateHero = ({
  mode = "portrait", // "portrait" | "square" | "landscape"
  tint,              // t.ex. theme.palette.primary.main
  strength = 0.18,
  fit = "width",     // nytt
  className,
}) => {
  const ratio =
    mode === "square" ? "1 / 1" : mode === "landscape" ? "16 / 9" : "3 / 5";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 640,
        ...(fit === "height" ? { height: "100%", display: "flex" } : { aspectRatio: ratio }),
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f4f0ff, #e9e3ff)",
        borderRadius: 3,
        overflow: "hidden",
      }}
      className={className}
    >
      <TemplateCreateSVG
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          width: fit === "height" ? "auto" : "92%",
          height: fit === "height" ? "92%" : "auto",
        }}
        aria-label="Skapa mall – tom mall med platshållare och inkommande innehåll"
      />

      {tint ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `
              radial-gradient(60% 60% at 75% 25%, ${alpha(tint, strength)} 0%, transparent 70%),
              linear-gradient(135deg, ${alpha(tint, strength * 0.7)} 0%, transparent 60%)
            `,
            mixBlendMode: "soft-light",
          }}
        />
      ) : null}
    </Box>
  );
};

/* =========================================
   TEAM WORKSPACE – samma fit-beteende
   ========================================= */
export const TeamWorkspaceHero = ({
  mode = "landscape", // "landscape" | "square" | "portrait"
  tint,               // t.ex. hero.ring
  strength = 0.2,
  fit = "width",      // nytt
  className,
}) => {
  const ratio =
    mode === "square" ? "1 / 1" : mode === "portrait" ? "3 / 4" : "16 / 9";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 640,
        ...(fit === "height" ? { height: "100%", display: "flex" } : { aspectRatio: ratio }),
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f4f0ff, #e9e3ff)",
        borderRadius: 3,
        overflow: "hidden",
      }}
      className={className}
    >
      {/* Viktigt: slice fyller ytan och beskär snyggt utan att stretcha */}
      <TeamWorkspaceSVG
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          width: fit === "height" ? "auto" : "90%",
          height: fit === "height" ? "90%" : "auto",
        }}
      />

      {/* Subtil accent-tint för att harmonisera mot hero-bakgrunden */}
      {tint ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `
              radial-gradient(60% 60% at 75% 25%, ${alpha(tint, strength)} 0%, transparent 70%),
              linear-gradient(135deg, ${alpha(tint, strength * 0.7)} 0%, transparent 60%)
            `,
            mixBlendMode: "soft-light",
          }}
        />
      ) : null}
    </Box>
  );
};

/* =========================================
   Hjälpramverk & övriga illustrationer
   ========================================= */

// --- Responsivt SVG-ramverk ---
const Frame = ({ children, w = 460, h = 260 }) => (
  <div
    style={{
      width: "100%",
      aspectRatio: `${w} / ${h}`, // fallback om container inte sätter höjd
      display: "grid",
      placeItems: "center",
    }}
  >
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  </div>
);

// Elegant, abstrakt hero – "Aurora Collab"
export const WorkspaceHeroAurora = ({ accent = "#7B61FF" }) => (
  <svg
    viewBox="0 0 460 260"
    width="100%"
    style={{ height: "auto", display: "block" }}
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Teamflöde som formar en ny arbetsyta"
  >
    <defs>
      {/* mjuka toningar som spelar fint mot din lila hero-bakgrund */}
      <linearGradient id="auroraTab" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F8F9FF" />
        <stop offset="1" stopColor="#FFFFFF" />
      </linearGradient>
      <linearGradient id="auroraAccent" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={accent} />
        <stop offset="1" stopColor={accent} />
      </linearGradient>
      <radialGradient id="glowDot" cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor={accent} stopOpacity="0.55" />
        <stop offset="1" stopColor={accent} stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Tunna aurora-svep (bara strokes) */}
    <g fill="none" stroke={accent} strokeOpacity="0.18">
      <path d="M22 210 C 120 188, 220 228, 430 218" />
      <path d="M34 64 C 180 26, 300 38, 436 62" />
      <path d="M62 144 C 160 122, 286 132, 414 146" />
    </g>

    {/* Central "canvas" – bräda med flikar/kolumner */}
    <g transform="translate(96,48)">
      <rect x="0" y="0" width="268" height="164" rx="16" fill="url(#auroraTab)" stroke="#E6E8F2" />
      <g opacity=".9">
        <rect x="18" y="14" width="56" height="10" rx="5" fill="#F0F2FA" />
        <rect x="78" y="14" width="68" height="10" rx="5" fill="#F0F2FA" />
        <rect x="150" y="14" width="48" height="10" rx="5" fill="#F0F2FA" />
      </g>

      {/* tre kolumner med kort */}
      <g>
        <rect x="16" y="36" width="78" height="118" rx="10" fill="#FAFBFF" stroke="#E6E8F2" />
        <rect x="96" y="36" width="78" height="118" rx="10" fill="#FAFBFF" stroke="#E6E8F2" />
        <rect x="176" y="36" width="78" height="118" rx="10" fill="#FAFBFF" stroke="#E6E8F2" />

        {/* kort */}
        <g>
          <rect x="24" y="46" width="62" height="26" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="24" y="78" width="62" height="26" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="24" y="110" width="62" height="26" rx="7" fill="#fff" stroke="#E6E8F2" />

          <rect x="104" y="54" width="62" height="30" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="104" y="92" width="62" height="30" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="104" y="130" width="62" height="18" rx="7" fill="#fff" stroke="#E6E8F2" />

          <rect x="184" y="50" width="62" height="26" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="184" y="82" width="62" height="42" rx="7" fill="#fff" stroke="#E6E8F2" />
          <rect x="184" y="130" width="62" height="22" rx="7" fill="#fff" stroke="#E6E8F2" />
        </g>

        {/* +-knapp i accent */}
        <g transform="translate(226,130)">
          <rect x="-12" y="-12" width="24" height="24" rx="7" fill="url(#auroraAccent)" />
          <path d="M0 -6 V6 M-6 0 H6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </g>
      </g>
    </g>

    {/* Tre “work-bubbles” (avatars) */}
    <g stroke="url(#auroraAccent)" strokeLinecap="round" strokeWidth="2" fill="none">
      <g transform="translate(60,96)">
        <circle cx="0" cy="0" r="14" fill="#fff" stroke={accent} strokeOpacity=".5" />
        <circle cx="0" cy="0" r="7" fill={accent} />
        <path d="M10 6 C 52 14, 98 24, 136 30" strokeDasharray="4 6" opacity=".7" />
      </g>
      <g transform="translate(324,28)">
        <circle cx="0" cy="0" r="14" fill="#fff" stroke={accent} strokeOpacity=".5" />
        <circle cx="0" cy="0" r="7" fill={accent} />
        <path d="M-10 12 C -24 42, -42 60, -92 74" strokeDasharray="6 8" opacity=".7" />
      </g>
      <g transform="translate(420,146)">
        <circle cx="0" cy="0" r="14" fill="#fff" stroke={accent} strokeOpacity=".5" />
        <circle cx="0" cy="0" r="7" fill={accent} />
        <path d="M-12 -6 C -48 2, -88 16, -124 28" strokeDasharray="4 6" opacity=".7" />
      </g>
    </g>

    {/* Distant UI-bricks */}
    <g opacity=".35" stroke={accent} fill="none">
      <rect x="372" y="206" width="64" height="34" rx="8" />
      <rect x="372" y="246" width="28" height="10" rx="5" />
      <rect x="404" y="246" width="28" height="10" rx="5" />
    </g>

    {/* Glödprickar i bakgrunden */}
    <circle cx="114" cy="224" r="10" fill="url(#glowDot)" />
    <circle cx="392" cy="88" r="10" fill="url(#glowDot)" />

    {/* små “sparkles” */}
    <g stroke={accent} strokeWidth="1.4" opacity=".6">
      <path d="M84 206 l6 0 M87 203 l0 6" strokeLinecap="round" />
      <path d="M382 90 l6 0 M385 87 l0 6" strokeLinecap="round" />
    </g>
  </svg>
);

// Palett hämtad ur "Meet Our Team-01.svg"
const TEAM_PALETTE = {
  bg: "#f4f9fd",
  stroke: "#0b2641",
  blue: "#14b3eb",
  indigo: "#175ae5",
  teal: "#12f0e5",
  cyan: "#7ee8fe",
  green: "#5cc654",
  yellow: "#fec85a",
  orange: "#ffc945",
  pink: "#fe84f2",
  magenta: "#ff5294",
  red: "#fe3941",
  white: "#fff",
  gray: "#ebf1f4",
};

/* Illustration i samma stil som Meet Our Team-01.svg, men MED en extra person (4 total) */
export const WorkspaceHeroMeetPlus = () => {
  const C = TEAM_PALETTE;
  return (
    <svg
      viewBox="0 0 480 480"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Team som skapar en arbetsyta"
    >
      {/* Cirkulär bakgrund */}
      <circle cx="240" cy="240" r="228" fill={C.bg} stroke={C.blue} strokeWidth="2" />

      {/* Subtila dekorblobs */}
      <g opacity=".12">
        <circle cx="120" cy="320" r="110" fill={C.indigo} />
        <circle cx="350" cy="140" r="120" fill={C.teal} />
      </g>

      {/* CENTRAL UI-BOARD */}
      <g transform="translate(100,130)">
        <rect width="280" height="160" rx="16" fill={C.white} stroke={C.gray} />
        <g transform="translate(16,16)">
          <circle r="4" fill={C.blue} />
          <circle cx="12" r="4" fill={C.blue} opacity=".65" />
          <circle cx="24" r="4" fill={C.blue} opacity=".35" />
        </g>
        {/* vänster nav/kolumn */}
        <g transform="translate(16,44)">
          <rect width="90" height="100" rx="10" fill="#FAFBFF" stroke={C.gray} />
          <circle cx="16" cy="18" r="6" fill={C.blue} />
          <rect x="32" y="13" width="46" height="10" rx="5" fill="#EEF2F7" />
          <circle cx="16" cy="46" r="6" fill={C.blue} opacity=".75" />
          <rect x="32" y="41" width="60" height="10" rx="5" fill="#EEF2F7" />
          <circle cx="16" cy="74" r="6" fill={C.blue} opacity=".5" />
          <rect x="32" y="69" width="50" height="10" rx="5" fill="#EEF2F7" />
        </g>
        {/* arbetsyta / kanban */}
        <g transform="translate(120,44)">
          <rect width="144" height="100" rx="10" fill={C.white} stroke={C.gray} />
          {/* tabs */}
          <rect x="10" y="10" width="46" height="10" rx="5" fill={C.blue} />
          <rect x="60" y="10" width="36" height="10" rx="5" fill="#EEF2F7" />
          <rect x="100" y="10" width="34" height="10" rx="5" fill="#EEF2F7" />
          {/* cards */}
          <rect x="10" y="32" width="60" height="18" rx="6" fill={C.white} stroke={C.gray} />
          <rect x="74" y="32" width="60" height="18" rx="6" fill={C.white} stroke={C.gray} />
          <rect x="10" y="56" width="46" height="18" rx="6" fill={C.white} stroke={C.gray} />
          <rect x="58" y="56" width="82" height="18" rx="6" fill={C.white} stroke={C.gray} />
          <rect x="10" y="80" width="86" height="14" rx="6" fill={C.white} stroke={C.gray} />
          {/* + knapp */}
          <g transform="translate(118,78)">
            <rect x="-11" y="-11" width="22" height="22" rx="7" fill={C.blue} />
            <path d="M-5 0H5M0-5V5" stroke={C.white} strokeWidth="2" strokeLinecap="round" />
          </g>
        </g>
      </g>

      {/* PERSON 1 */}
      <g transform="translate(62,306)">
        <path d="M-6 -8 q18 -18 36 0 v30 q-16 12 -32 0 z" fill={C.indigo} />
        <circle cx="12" cy="-36" r="12" fill="#FFD8B5" stroke={C.stroke} strokeWidth="1" />
        <path d="M4 -41 q8 -9 18 -4 q-6 0 -12 2 q-4 1 -6 1" fill={C.stroke} />
        <circle cx="9" cy="-36" r="1.2" fill={C.stroke} />
        <circle cx="15" cy="-36" r="1.2" fill={C.stroke} />
        <path d="M9 -32 q3 2 6 0" stroke={C.stroke} strokeWidth="1" fill="none" />
        <path d="M26 -14 Q86 -36 128 -52" stroke={C.indigo} strokeWidth="5.5" strokeLinecap="round" fill="none" />
      </g>

      {/* PERSON 2 */}
      <g transform="translate(240,370)">
        <path d="M-20 -10 q20 -18 40 0 v30 q-20 14 -40 0 z" fill={C.cyan} />
        <circle cx="0" cy="-38" r="13" fill="#FFE3B5" stroke={C.stroke} strokeWidth="1" />
        <path d="M-8 -42 q8 -8 18 -3 q-6 0 -12 2 q-4 1 -6 1" fill={C.stroke} />
        <circle cx="-4.2" cy="-38" r="1.2" fill={C.stroke} />
        <circle cx="4.2" cy="-38" r="1.2" fill={C.stroke} />
        <path d="M-4 -34 q4 2 8 0" stroke={C.stroke} strokeWidth="1" fill="none" />
        <rect x="-8" y="-6" width="16" height="12" rx="3" fill={C.white} stroke={C.gray} />
        <path d="M-20 -4 q-14 10 -10 22" stroke={C.cyan} strokeWidth="5" strokeLinecap="round" />
        <path d="M20 -4 q14 10 10 22" stroke={C.cyan} strokeWidth="5" strokeLinecap="round" />
      </g>

      {/* PERSON 3 */}
      <g transform="translate(414,292)">
        <path d="M-18 -10 q18 -18 36 0 v28 q-18 14 -36 0 z" fill={C.pink} />
        <circle cx="0" cy="-36" r="12" fill="#F4D2FF" stroke={C.stroke} strokeWidth="1" />
        <path d="M-8 -40 q8 -8 17 -3 q-6 0 -11 2 q-4 1 -6 1" fill={C.stroke} />
        <circle cx="-3.5" cy="-35" r="1.2" fill={C.stroke} />
        <circle cx="3.5" cy="-35" r="1.2" fill={C.stroke} />
        <path d="M-4 -31 q3 2 6 0" stroke={C.stroke} strokeWidth="1" fill="none" />
        <path d="M10 -12 Q30 -26 22 -42" stroke={C.pink} strokeWidth="5.5" strokeLinecap="round" fill="none" />
      </g>

      {/* PERSON 4 */}
      <g transform="translate(126,164)">
        <line x1="-10" y1="14" x2="18" y2="14" stroke={C.gray} strokeWidth="1" />
        <path d="M-10 8 q18 -18 36 0 v26 q-18 14 -36 0 z" fill={C.green} />
        <circle cx="8" cy="-16" r="10.5" fill="#FFD8B5" stroke={C.stroke} strokeWidth="1" />
        <path d="M2 -20 q8 -8 16 -3 q-5 0 -10 2 q-3 1 -6 1" fill={C.stroke} />
        <circle cx="4.5" cy="-16" r="1" fill={C.stroke} />
        <circle cx="11.5" cy="-16" r="1" fill={C.stroke} />
        <path d="M4.5 -12 q3 2 6 0" stroke={C.stroke} strokeWidth=".9" fill="none" />
        <rect x="22" y="-2" width="24" height="14" rx="4" fill={C.white} stroke={C.gray} />
        <path d="M6 4 Q26 -2 36 -6" stroke={C.green} strokeWidth="5" strokeLinecap="round" />
      </g>

      {/* Små UI-chips */}
      <g opacity=".45" stroke={C.gray} fill="none">
        <rect x="366" y="368" width="60" height="26" rx="8" />
        <rect x="366" y="402" width="26" height="10" rx="5" />
        <rect x="396" y="402" width="26" height="10" rx="5" />
      </g>
    </svg>
  );
};

export const WorkspaceHeroCollab = () => (
  <svg
    viewBox="0 0 460 260"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "100%", height: "100%" }}
  >
    <defs>
      <radialGradient id="blob1" cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor="#7B61FF" stopOpacity="0.35" />
        <stop offset="1" stopColor="#7B61FF" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="blob2" cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor="#00C6FF" stopOpacity="0.25" />
        <stop offset="1" stopColor="#00C6FF" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="iconGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#7B61FF" />
        <stop offset="1" stopColor="#00C6FF" />
      </linearGradient>
    </defs>

    {/* bakgrundsblobs */}
    <circle cx="120" cy="180" r="120" fill="url(#blob1)" />
    <circle cx="340" cy="80" r="140" fill="url(#blob2)" />

    {/* aurora-känsla */}
    <circle cx="230" cy="130" r="100" stroke="#7B61FF" strokeOpacity="0.15" fill="none" />
    <circle cx="230" cy="130" r="70" stroke="#7B61FF" strokeOpacity="0.1" fill="none" />

    {/* central arbetsyta */}
    <g transform="translate(180,80)">
      <rect width="100" height="80" rx="12" fill="url(#iconGrad)" opacity="0.9" />
      <rect x="16" y="20" width="70" height="10" rx="5" fill="#fff" opacity="0.9" />
      <rect x="16" y="38" width="70" height="10" rx="5" fill="#fff" opacity="0.9" />
      <rect x="16" y="56" width="40" height="10" rx="5" fill="#fff" opacity="0.9" />

      {/* plus-knapp */}
      <g transform="translate(80,10)">
        <circle r="10" fill="#fff" />
        <path d="M-4 0 H4 M0 -4 V4" stroke="#7B61FF" strokeWidth="2" strokeLinecap="round" />
      </g>
    </g>

    {/* avatarer runtom */}
    <g fill="#fff" stroke="url(#iconGrad)" strokeWidth="2">
      <circle cx="80" cy="60" r="14" />
      <circle cx="390" cy="60" r="14" />
      <circle cx="80" cy="200" r="14" />
      <circle cx="380" cy="200" r="14" />
    </g>

    {/* linjer mellan avatarer och arbetsyta */}
    <path
      d="M94 60 Q180 80 180 120"
      stroke="url(#iconGrad)"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
    />
    <path
      d="M376 60 Q280 80 280 120"
      stroke="url(#iconGrad)"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
    />
    <path
      d="M94 200 Q180 180 180 140"
      stroke="url(#iconGrad)"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
    />
    <path
      d="M366 200 Q280 180 280 140"
      stroke="url(#iconGrad)"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
    />
  </svg>
);

/** 1) Workspace hero: modular blocks that is built together */
export const WorkspaceHero = ({ accent = "#7B61FF" }) => (
  <Frame w={460} h={260}>
    <defs>
      <linearGradient id="whBtn" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={accent} />
        <stop offset="1" stopColor={accent} stopOpacity="0.85" />
      </linearGradient>
      <filter id="whSoft" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <style>{`
        @keyframes whFloatA { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes whFloatB { 0%,100%{transform:translateY(0)} 50%{transform:translateY(5px)} }
        @keyframes whFloatC { 0%,100%{transform:translate(0,0)} 50%{transform:translate(6px,-4px)} }
        .whA{ animation: whFloatA 6s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 50%; }
        .whB{ animation: whFloatB 7s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 50%; }
        .whC{ animation: whFloatC 8s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 50%; }
        .whCard{ fill:#fff; stroke:#E6E8F2; }
        .whChip{ fill:#F6F7FB; stroke:#E6E8F2; }
      `}</style>
    </defs>

    {/* subtila hjälpringar */}
    <g opacity=".35" stroke={accent} fill="none">
      <circle cx="365" cy="54" r="42" />
      <circle cx="366" cy="206" r="70" />
    </g>

    {/* tre modul-kolumner */}
    <g transform="translate(64,40)">
      <rect x="0" y="0" width="104" height="180" rx="12" fill="#FAFBFF" stroke="#E6E8F2" />
      <rect x="128" y="0" width="104" height="180" rx="12" fill="#FAFBFF" stroke="#E6E8F2" />
      <rect x="256" y="0" width="104" height="180" rx="12" fill="#FAFBFF" stroke="#E6E8F2" />

      {/* kort i kolumnerna */}
      <g className="whA">
        <rect x="12" y="18" width="80" height="34" rx="8" className="whCard" />
        <rect x="12" y="62" width="80" height="34" rx="8" className="whCard" />
        <rect x="12" y="106" width="80" height="34" rx="8" className="whCard" />
      </g>
      <g className="whB">
        <rect x="140" y="26" width="80" height="40" rx="8" className="whCard" />
        <rect x="140" y="74" width="80" height="40" rx="8" className="whCard" />
        <rect x="140" y="122" width="80" height="34" rx="8" className="whCard" />
      </g>
      <g className="whC">
        <rect x="268" y="22" width="80" height="34" rx="8" className="whCard" />
        <rect x="268" y="64" width="80" height="56" rx="8" className="whCard" />
        <rect x="268" y="126" width="80" height="34" rx="8" className="whCard" />
      </g>

      {/* labels */}
      <g>
        <rect x="22" y="28" width="36" height="8" rx="4" className="whChip" />
        <rect x="150" y="36" width="48" height="10" rx="5" className="whChip" />
        <rect x="278" y="72" width="56" height="10" rx="5" className="whChip" />
      </g>

      {/* CTA */}
      <g transform="translate(212,162)" filter="url(#whSoft)">
        <rect x="-18" y="-18" width="36" height="36" rx="10" fill="url(#whBtn)" />
        <path d="M0 -8 V8 M-8 0 H8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      </g>

      {/* streckad kontur */}
      <rect
        x="8"
        y="152"
        width="88"
        height="16"
        rx="8"
        fill="none"
        stroke={accent}
        opacity=".45"
        strokeDasharray="6 6"
      />
    </g>

    {/* sparkles */}
    <g stroke={accent} strokeWidth="1.5" opacity=".6">
      <path d="M382 86 l6 0 M385 83 l0 6" strokeLinecap="round" />
      <path d="M95 206 l6 0 M98 203 l0 6" strokeLinecap="round" />
    </g>
  </Frame>
);

/** 2) Guide board: quadrant of selectable tiles with accent dots */
export const GuideBoard = () => (
  <Frame w={440} h={240}>
    <rect x="16" y="16" width="408" height="208" rx="14" fill="#FFFFFF" stroke="#E6E8F2" />
    <rect x="36" y="34" width="168" height="72" rx="12" fill="#EEF2FF" stroke="#5B4DF2" />
    <circle cx="58" cy="54" r="8" fill="#5B4DF2" />
    <rect x="236" y="34" width="168" height="72" rx="12" fill="#F0FDF4" stroke="#10B981" />
    <circle cx="258" cy="54" r="8" fill="#10B981" />
    <rect x="36" y="134" width="168" height="72" rx="12" fill="#FFF7ED" stroke="#F59E0B" />
    <circle cx="58" cy="154" r="8" fill="#F59E0B" />
    <rect x="236" y="134" width="168" height="72" rx="12" fill="#FDF2F8" stroke="#D946EF" />
    <circle cx="258" cy="154" r="8" fill="#D946EF" />
    <rect x="180" y="108" width="80" height="8" rx="4" fill="#E6E8F2" />
  </Frame>
);

/** 3) UsersInvite: three avatars connected + big "add user" badge */
export const UsersInvite = () => (
  <Frame w={440} h={240}>
    <defs>
      <clipPath id="avatarClip">
        <circle cx="0" cy="0" r="18" />
      </clipPath>
    </defs>
    {/* network lines */}
    <path
      d="M120 96 C170 60, 270 60, 320 96"
      stroke="#CBD5E1"
      strokeWidth="2"
      fill="none"
      strokeDasharray="6 6"
    />
    <path
      d="M120 96 C170 130, 270 130, 320 96"
      stroke="#CBD5E1"
      strokeWidth="2"
      fill="none"
      strokeDasharray="6 6"
    />
    {/* avatars */}
    <g transform="translate(120,96)">
      <circle r="26" fill="#EEF2FF" stroke="#5B4DF2" strokeWidth="2" />
      <g clipPath="url(#avatarClip)" transform="translate(0,0)">
        <circle r="18" fill="#C7D2FE" />
        <rect x="-9" y="-4" width="18" height="12" rx="6" fill="#93C5FD" />
      </g>
    </g>
    <g transform="translate(220,140)">
      <circle r="26" fill="#FFF1F2" stroke="#EF4444" strokeWidth="2" />
      <g clipPath="url(#avatarClip)">
        <circle r="18" fill="#FECACA" />
        <rect x="-9" y="-4" width="18" height="12" rx="6" fill="#FDA4AF" />
      </g>
    </g>
    <g transform="translate(320,96)">
      <circle r="26" fill="#F0FDF4" stroke="#10B981" strokeWidth="2" />
      <g clipPath="url(#avatarClip)">
        <circle r="18" fill="#A7F3D0" />
        <rect x="-9" y="-4" width="18" height="12" rx="6" fill="#6EE7B7" />
      </g>
    </g>
    {/* add badge */}
    <g transform="translate(384,56)">
      <circle r="18" fill="#EEF2FF" stroke="#3B82F6" strokeWidth="2" />
      <path d="M0 -8 V 8 M -8 0 H 8" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
    </g>
  </Frame>
);

/** 4) Columns & Views: three column blocks to suggest layout selection */
export const ColumnsViews = () => (
  <Frame w={460} h={260}>
    <rect x="18" y="20" width="424" height="220" rx="14" fill="#FFFFFF" stroke="#E6E8F2" />
    <rect x="40" y="40" width="112" height="168" rx="10" fill="#EEF2FF" stroke="#5B4DF2" />
    <rect x="80" y="60" width="72" height="10" rx="5" fill="#E6EAFE" />
    <rect x="64" y="80" width="88" height="10" rx="5" fill="#EEF2FF" />
    <rect x="176" y="40" width="112" height="168" rx="10" fill="#F0FDF4" stroke="#10B981" />
    <rect x="216" y="60" width="72" height="10" rx="5" fill="#DCFCE7" />
    <rect x="200" y="80" width="88" height="10" rx="5" fill="#F0FDF4" />
    <rect x="312" y="40" width="112" height="168" rx="10" fill="#FFF7ED" stroke="#F59E0B" />
    <rect x="352" y="60" width="72" height="10" rx="5" fill="#FEF3C7" />
    <rect x="336" y="80" width="88" height="10" rx="5" fill="#FFFBEB" />
  </Frame>
);
